import { useState, useEffect } from "react";
import { useDropboxBackup } from "../hooks/useDropboxBackup";
import type { CanvasData, CanvasMetadata } from "../data/storage";
import type { 
  AppState, 
  BinaryFiles, 
  ExcalidrawImperativeAPI 
} from "@excalidraw/excalidraw/types";
import type { OrderedExcalidrawElement } from "@excalidraw/element/types";
import "./DropboxBackupWidget.scss";

interface DropboxBackupWidgetProps {
  excalidrawAPI?: ExcalidrawImperativeAPI | null;
}

export const DropboxBackupWidget = ({
  excalidrawAPI,
}: DropboxBackupWidgetProps) => {
  const { adapter, isAuthenticated, startAuth, disconnect } =
    useDropboxBackup();
  const [lastBackup, setLastBackup] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCanvasList, setShowCanvasList] = useState(false);
  const [canvasList, setCanvasList] = useState<CanvasMetadata[]>([]);
  const [listMode, setListMode] = useState<"restore" | "update">("restore");

  useEffect(() => {
    const storedDate = localStorage.getItem("dropbox-last-backup");
    if (storedDate) {
      setLastBackup(storedDate);
    }
  }, []);

  const handleBackup = async (updateExisting = false) => {
    if (!adapter || !excalidrawAPI) {
      alert("Dropbox belum terhubung atau Excalidraw belum siap");
      return;
    }

    setIsLoading(true);
    try {
      const elements = excalidrawAPI.getSceneElements();
      const appState = excalidrawAPI.getAppState();
      const files = excalidrawAPI.getFiles();
      
      // Validate that we have something to backup
      if (!elements || elements.length === 0) {
        if (!confirm("Canvas kosong. Tetap backup?")) {
          setIsLoading(false);
          return;
        }
      }

      // If update mode, show canvas list to choose which to update
      if (updateExisting) {
        await handleUpdateCanvas();
        return;
      }
      
      // Generate canvas name from timestamp if not set
      const canvasName = appState.name || 
        `Canvas ${new Date().toLocaleString("id-ID", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })}`;
      
      const data: CanvasData = {
        elements: elements as readonly OrderedExcalidrawElement[],
        appState: {
          ...appState,
          name: canvasName,
          width: appState.width || 0,
          height: appState.height || 0,
          offsetLeft: appState.offsetLeft || 0,
          offsetTop: appState.offsetTop || 0,
        } as AppState,
        files,
      };
      
      const metadata = await adapter.createCanvas(data);
      const now = new Date().toISOString();
      setLastBackup(now);
      localStorage.setItem("dropbox-last-backup", now);
      alert(`✅ Backup berhasil!\nNama: ${metadata.name}\nID: ${metadata.id}`);
    } catch (error) {
      console.error("Backup error:", error);
      const errorMsg = error instanceof Error ? error.message : String(error);
      alert("❌ Backup gagal:\n" + errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateCanvas = async () => {
    await loadCanvasList("update");
  };

  const handleUpdateCanvasById = async (canvasId: string, canvasName: string) => {
    if (!adapter || !excalidrawAPI) return;

    if (!confirm(`Update canvas "${canvasName}"?\nCanvas ini akan di-overwrite dengan data saat ini.`)) {
      return;
    }

    setIsLoading(true);
    try {
      const elements = excalidrawAPI.getSceneElements();
      const appState = excalidrawAPI.getAppState();
      const files = excalidrawAPI.getFiles();

      const data: CanvasData = {
        elements: elements as readonly OrderedExcalidrawElement[],
        appState: {
          ...appState,
          name: canvasName, // Keep original name
          width: appState.width || 0,
          height: appState.height || 0,
          offsetLeft: appState.offsetLeft || 0,
          offsetTop: appState.offsetTop || 0,
        } as AppState,
        files,
      };

      await adapter.saveCanvas(canvasId, data);
      
      const now = new Date().toISOString();
      setLastBackup(now);
      localStorage.setItem("dropbox-last-backup", now);
      
      setShowCanvasList(false);
      alert(`✅ Canvas "${canvasName}" berhasil di-update!`);
    } catch (error) {
      console.error("Update error:", error);
      alert("❌ Update gagal: " + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCanvasList = async (mode: "restore" | "update" = "restore") => {
    if (!adapter) return;

    setIsLoading(true);
    try {
      const canvases = await adapter.listCanvases();
      if (canvases.length === 0) {
        alert("Tidak ada backup ditemukan");
        return;
      }

      // Sort by date (newest first)
      const sorted = canvases.sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
      );

      setListMode(mode);
      setCanvasList(sorted);
      setShowCanvasList(true);
    } catch (error) {
      console.error("Load canvas list error:", error);
      alert("❌ Gagal memuat daftar canvas: " + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestoreCanvas = async (canvasId: string, canvasName: string) => {
    if (!adapter || !excalidrawAPI) return;

    setIsLoading(true);
    try {
      const data = await adapter.loadCanvas(canvasId);

      if (data) {
        excalidrawAPI.updateScene({
          elements: data.elements,
          appState: data.appState,
        });

        if (data.files) {
          excalidrawAPI.addFiles(Object.values(data.files));
        }

        setShowCanvasList(false);
        alert(`✅ Restore berhasil: ${canvasName}`);
      }
    } catch (error) {
      console.error("Restore error:", error);
      alert("❌ Restore gagal: " + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCanvas = async (canvasId: string, canvasName: string) => {
    if (!adapter) return;

    if (!confirm(`Hapus canvas "${canvasName}"?\nTindakan ini tidak dapat dibatalkan!`)) {
      return;
    }

    setIsLoading(true);
    try {
      await adapter.deleteCanvas(canvasId);
      
      // Refresh list
      const updated = canvasList.filter((c) => c.id !== canvasId);
      setCanvasList(updated);
      
      if (updated.length === 0) {
        setShowCanvasList(false);
      }
      
      alert(`✅ Canvas "${canvasName}" berhasil dihapus`);
    } catch (error) {
      console.error("Delete error:", error);
      alert("❌ Gagal menghapus: " + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="dropbox-widget">
      <button
        className={`widget-toggle ${isExpanded ? "expanded" : ""}`}
        onClick={() => setIsExpanded(!isExpanded)}
        title={isExpanded ? "Hide Dropbox" : "Show Dropbox"}
      >
        {isExpanded ? "✕" : "📦"}
      </button>

      <div className={`widget-content ${isExpanded ? "visible" : ""}`}>
        {!isAuthenticated ? (
          <button className="dropbox-connect-btn" onClick={startAuth}>
            Connect Dropbox
          </button>
        ) : showCanvasList ? (
          <div className="canvas-list">
            <div className="canvas-list-header">
              <h3>
                {listMode === "restore" ? "� Pilih Canvas untuk Restore" : "📝 Pilih Canvas untuk Update"}
              </h3>
              <button
                className="close-list-btn"
                onClick={() => setShowCanvasList(false)}
              >
                ✕
              </button>
            </div>
            <div className="canvas-items">
              {canvasList.map((canvas) => (
                <div key={canvas.id} className="canvas-item">
                  <div className="canvas-info">
                    <div className="canvas-name">{canvas.name}</div>
                    <div className="canvas-date">
                      {formatDate(canvas.updatedAt)}
                    </div>
                  </div>
                  <div className="canvas-actions">
                    {listMode === "restore" ? (
                      <>
                        <button
                          className="restore-canvas-btn"
                          onClick={() =>
                            handleRestoreCanvas(canvas.id, canvas.name)
                          }
                          disabled={isLoading}
                          title="Restore canvas ini"
                        >
                          ↺
                        </button>
                        <button
                          className="delete-canvas-btn"
                          onClick={() =>
                            handleDeleteCanvas(canvas.id, canvas.name)
                          }
                          disabled={isLoading}
                          title="Hapus canvas ini"
                        >
                          🗑
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="update-canvas-btn"
                          onClick={() =>
                            handleUpdateCanvasById(canvas.id, canvas.name)
                          }
                          disabled={isLoading}
                          title="Update canvas ini dengan data saat ini"
                        >
                          💾
                        </button>
                        <button
                          className="delete-canvas-btn"
                          onClick={() =>
                            handleDeleteCanvas(canvas.id, canvas.name)
                          }
                          disabled={isLoading}
                          title="Hapus canvas ini"
                        >
                          🗑
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {lastBackup && (
              <div className="last-backup">
                Backup terakhir: {formatDate(lastBackup)}
              </div>
            )}
            <div className="backup-actions">
              <button
                className="backup-btn"
                onClick={() => handleBackup(false)}
                disabled={isLoading}
              >
                {isLoading ? "⏳ Processing..." : "💾 Backup Baru"}
              </button>
              <button
                className="update-btn"
                onClick={() => handleBackup(true)}
                disabled={isLoading}
              >
                {isLoading ? "⏳ Loading..." : "📝 Update Canvas"}
              </button>
              <button
                className="restore-btn"
                onClick={() => loadCanvasList("restore")}
                disabled={isLoading}
              >
                {isLoading ? "⏳ Loading..." : "📥 Restore Canvas"}
              </button>
            </div>
            <button className="disconnect-btn" onClick={disconnect}>
              🔌 Disconnect
            </button>
          </>
        )}
      </div>
    </div>
  );
};
